package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The contract's enum is exactly `ios` | `android`, on both `PushToken` and its request.
 *
 * [UNKNOWN] exists because every wire enum in this module has one, but the `platform` properties
 * below deliberately carry NO default -- and `coerceInputValues` only coerces to a property's
 * declared default, so without one an unrecognised value is a decode failure. That is the safe
 * direction here: `push_tokens` has `CHECK (platform IN ('android','ios'))`, so a value that
 * coerced to `unknown` would pass validation and then fail at the INSERT as a 500 internal_error.
 * Failing at decode gives the same 400 the rest of the server returns for a malformed body.
 */
@Serializable
enum class DevicePlatform {
    @SerialName("ios") IOS,
    @SerialName("android") ANDROID,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `RegisterPushTokenRequest` -- `POST /push-tokens`, operationId `registerPushToken`.
 *
 * [deviceId] is what identifies an installation across token rotations: FCM issues a new token
 * whenever it feels like it (reinstall, restore, cleared data), and without a stable device id
 * the server accumulates one dead row per rotation and eventually fans every message out to a
 * pile of tokens that no longer exist.
 *
 * It must be a value the CLIENT mints once and persists -- a random UUID in its own store. Not a
 * hardware id, not `Build.MODEL`, and never anything shared between two installations: the
 * server treats (user, device_id) as unique, so a colliding id makes one device's registration
 * silently overwrite the other's, and the loser stops receiving notifications with a clean 201
 * on every attempt and nothing anywhere reporting it.
 */
@Serializable
data class RegisterPushTokenRequest(
    @SerialName("token") val token: String,
    @SerialName("platform") val platform: DevicePlatform,
    /** `maxLength: 200` in the contract. */
    @SerialName("device_id") val deviceId: String,
)

/**
 * `PushToken` -- the 201 body of `registerPushToken`.
 *
 * Note what is NOT here: the token itself. The contract deliberately does not echo it, and the
 * server treats a registration token as bearer-grade -- whoever holds it can receive that
 * device's notifications -- so it is never returned, never logged, and never written anywhere
 * but its own column.
 */
@Serializable
data class PushToken(
    @SerialName("id") val id: Uuid,
    @SerialName("platform") val platform: DevicePlatform,
    @SerialName("device_id") val deviceId: String,
    @SerialName("created_at") val createdAt: Timestamp,
)

/**
 * `NotificationSettings` -- `GET`/`PATCH /users/me/notification-settings`.
 *
 * The contract `$ref`s this same schema for the PATCH body, and six of the eight fields are
 * `required`, so a conforming request carries all six. They therefore have NO Kotlin default:
 * with one, a body omitting `messages` would decode as `messages = true` and silently switch a
 * user's notifications back on, answering 200 with the value they had just turned off.
 *
 * The two optional fields keep defaults because they are optional on the wire and the response
 * must always carry them. Absence in a PATCH means "leave unchanged", which a default cannot
 * express -- so the SERVER decodes requests into its own view with nullable optionals and
 * merges. Reusing this type for the request body would write `false` over a setting the client
 * merely did not mention.
 *
 * [security] and [mutedChatMentions] are `const: true` in the contract ("Critical security
 * notifications. Always enabled in MVP." / "Muted chats may still notify for mentions."), so the
 * server never writes them from a request body and always answers true. A client that PATCHes
 * `security: false` gets `security: true` back, and that is the contract working, not a bug.
 */
@Serializable
data class NotificationSettings(
    @SerialName("messages") val messages: Boolean,
    @SerialName("groups") val groups: Boolean,
    @SerialName("discover") val discover: Boolean,
    @SerialName("mentions") val mentions: Boolean,
    /** `const: true`. Required, so no default; never written from a request body. */
    @SerialName("security") val security: Boolean,
    /** When false the push carries neither the sender nor any message text. */
    @SerialName("show_message_preview") val showMessagePreview: Boolean,
    /** Optional in the contract, default false: "generic text such as `Alex commented on your post`". */
    @SerialName("show_discover_comment_preview") val showDiscoverCommentPreview: Boolean = false,
    /** `const: true`. Never written from a request body. */
    @SerialName("muted_chat_mentions") val mutedChatMentions: Boolean = true,
    /** When true, the user receives daily Streak Saver warnings and partner nudges before their streak breaks. */
    @SerialName("streaks") val streaks: Boolean = true,
)
